import { useEffect, useMemo, useRef, useCallback, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  HashRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { ProcessingProvider } from "@/contexts/ProcessingContext";
import { AnimatePresence } from "framer-motion";
import { PageTransition } from "@/components/PageTransition";
import { useNotificationHandler } from "@/hooks/useNotificationHandler";
import { usePushNotificationSetup } from "@/hooks/usePushNotificationSetup";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { isNativeRuntime } from "@/lib/nativeRuntime";
import { sanitizeOAuthParams, hasOAuthParams } from "@/lib/urlSanitizer";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

// Critical pages - eagerly loaded for fast initial render
import Index from "./pages/Index";
import Boot from "./pages/Boot";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import NativeCallback from "./pages/NativeCallback";
import NotFound from "./pages/NotFound";
import SpaRedirector from "./components/SpaRedirector";
import CanonicalEnforcer from "./components/CanonicalEnforcer";

// Lazy loaded pages - code split with automatic retry on chunk load failure
const Splash = lazyWithRetry(() => import("./pages/Splash"));
const AdFlowManager = lazyWithRetry(() => import("./components/AdFlowManager").then(m => ({ default: m.AdFlowManager })));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const Home = lazyWithRetry(() => import("./pages/Home"));
const ScanProgress = lazyWithRetry(() => import("./pages/ScanProgress"));
const Results = lazyWithRetry(() => import("./pages/Results"));
const SavedGifts = lazyWithRetry(() => import("./pages/SavedGifts"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const About = lazyWithRetry(() => import("./pages/About"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const Paywall = lazyWithRetry(() => import("./pages/Paywall"));
const AdminPartners = lazyWithRetry(() => import("./pages/AdminPartners"));
const AdminEmailAnalytics = lazyWithRetry(() => import("./pages/AdminEmailAnalytics"));
const Partners = lazyWithRetry(() => import("./pages/Partners"));
const PartnerSuccess = lazyWithRetry(() => import("./pages/PartnerSuccess"));
const PartnerDashboard = lazyWithRetry(() => import("./pages/PartnerDashboard"));
const DeleteAccount = lazyWithRetry(() => import("./pages/DeleteAccount"));

// SEO Landing Pages - lazy loaded with retry
const AIGiftApp = lazyWithRetry(() => import("./pages/seo/AIGiftApp"));
const GiftFromPhoto = lazyWithRetry(() => import("./pages/seo/GiftFromPhoto"));
const PhotoGiftFinder = lazyWithRetry(() => import("./pages/seo/PhotoGiftFinder"));
const GiftGeneratorIPhone = lazyWithRetry(() => import("./pages/seo/GiftGeneratorIPhone"));
const PersonalizedGiftApp = lazyWithRetry(() => import("./pages/seo/PersonalizedGiftApp"));
const AIGiftFinder = lazyWithRetry(() => import("./pages/seo/AIGiftFinder"));
const GiftIdeasByPersonality = lazyWithRetry(() => import("./pages/seo/GiftIdeasByPersonality"));
const LastMinuteGiftIdeas = lazyWithRetry(() => import("./pages/seo/LastMinuteGiftIdeas"));
const BirthdayGiftIdeas = lazyWithRetry(() => import("./pages/seo/BirthdayGiftIdeas"));
const AnniversaryGiftIdeas = lazyWithRetry(() => import("./pages/seo/AnniversaryGiftIdeas"));
const ValentinesDayGifts = lazyWithRetry(() => import("./pages/seo/ValentinesDayGifts"));
const ChristmasGiftIdeas = lazyWithRetry(() => import("./pages/seo/ChristmasGiftIdeas"));
const GiftsForHer = lazyWithRetry(() => import("./pages/seo/GiftsForHer"));
const GiftsForHim = lazyWithRetry(() => import("./pages/seo/GiftsForHim"));
const BestWhiteElephantGifts = lazyWithRetry(() => import("./pages/seo/BestWhiteElephantGifts"));
const BestWeddingGifts = lazyWithRetry(() => import("./pages/seo/BestWeddingGifts"));
const BestHousewarmingGifts = lazyWithRetry(() => import("./pages/seo/BestHousewarmingGifts"));
const BestBirthdayGiftForBestFriend = lazyWithRetry(() => import("./pages/seo/BestBirthdayGiftForBestFriend"));
const BestEngagementGifts = lazyWithRetry(() => import("./pages/seo/BestEngagementGifts"));

// Loading fallback for lazy loaded routes
const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const queryClient = new QueryClient();

// Helper to check if we're in a native environment
const isNativeEnv = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent?.toLowerCase?.() ?? "";
  return ua.includes("despia") || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform());
};

// Initialize global rewarded ad callbacks EARLY for Despia/Capacitor native runtime
if (typeof window !== "undefined") {
  (window as any).updateRewardedStatus = (status: string) => {
    if (!isNativeEnv()) {
      console.warn("[RewardedAd] Unauthorized callback attempt from non-native environment");
      return;
    }
    console.log("[RewardedAd] Global callback received, status:", status);
    const event = new CustomEvent("rewardedAdComplete", { detail: { status } });
    window.dispatchEvent(event);
  };

  (window as any).adLoadFailed = (reason?: string) => {
    if (!isNativeEnv()) {
      console.warn("[RewardedAd] Unauthorized adLoadFailed callback attempt from non-native environment");
      return;
    }
    console.error("[RewardedAd] Ad load failed, reason:", reason);
    const event = new CustomEvent("rewardedAdFailed", {
      detail: { error: reason || "Ad failed to load", source: "adLoadFailed" },
    });
    window.dispatchEvent(event);
  };

  (window as any).adDismissed = () => {
    if (!isNativeEnv()) {
      console.warn("[RewardedAd] Unauthorized adDismissed callback attempt from non-native environment");
      return;
    }
    console.log("[RewardedAd] Ad dismissed by user");
    const event = new CustomEvent("rewardedAdDismissed", { detail: { timestamp: Date.now() } });
    window.dispatchEvent(event);
  };

  (window as any).adClosed = (wasCompleted?: boolean) => {
    if (!isNativeEnv()) {
      console.warn("[RewardedAd] Unauthorized adClosed callback attempt from non-native environment");
      return;
    }
    console.log("[RewardedAd] Ad closed, wasCompleted:", wasCompleted);
    const event = new CustomEvent("rewardedAdClosed", {
      detail: { wasCompleted: !!wasCompleted, timestamp: Date.now() },
    });
    window.dispatchEvent(event);
  };

  console.log("[RewardedAd] Global ad callbacks initialized (native env check includes Capacitor)");
}

const RouterGate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const native = useMemo(() => isNativeRuntime(), []);

  useEffect(() => {
    if (hasOAuthParams()) {
      console.log('[RouterGate] OAuth params detected, skipping sanitize to let Auth handle them');
    } else {
      sanitizeOAuthParams();
    }
  }, []);

  useEffect(() => {
    if (!native) return;
    if (location.pathname === "/" || location.pathname === "") {
      console.log('[RouterGate] Native app at root, redirecting to /boot');
      navigate("/boot", { replace: true });
    }
  }, [location.pathname, navigate, native]);

  return null;
};

// FIX: Removed resumeKey state and all its bump listeners from AnimatedRoutes.
// Every resume event (pageshow, visibilitychange, Capacitor appStateChange/resume)
// was giving <Routes> a new key, which unmounted and remounted the entire route
// tree — destroying scroll position, component state, in-flight requests, and
// causing a visible flash on every background/foreground transition.
// The forced remount was redundant: AppStateRecovery handles long-idle recovery
// by navigating to /boot. Routes key is now just location.pathname — stable
// per-route, not per-resume.
const AnimatedRoutes = () => {
  const location = useLocation();
  const native = useMemo(() => isNativeRuntime(), []);

  useNotificationHandler();
  usePushNotificationSetup();

  const routes = (
    <Suspense fallback={<LazyFallback />}>
      <Routes
        location={location}
        key={location.pathname}
      >
        <Route path="/" element={<PageTransition><Index /></PageTransition>} />
        <Route path="/boot/*" element={<PageTransition><Boot /></PageTransition>} />
        <Route path="/splash" element={<PageTransition><Splash /></PageTransition>} />
        <Route path="/onboarding" element={<PageTransition><Onboarding /></PageTransition>} />
        <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
        {/* Native OAuth deeplink route - maps to Auth for token processing */}
        <Route path="/oauth/auth" element={<PageTransition><Auth /></PageTransition>} />
        <Route path="/auth/callback" element={<PageTransition><AuthCallback /></PageTransition>} />
        <Route path="/native-callback" element={<PageTransition><NativeCallback /></PageTransition>} />
        <Route path="/home" element={<PageTransition><Home /></PageTransition>} />
        <Route path="/scan-progress" element={<PageTransition><ScanProgress /></PageTransition>} />
        <Route path="/results" element={<PageTransition><Results /></PageTransition>} />
        <Route path="/saved-gifts" element={<PageTransition><SavedGifts /></PageTransition>} />
        <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
        <Route path="/terms-of-service" element={<PageTransition><TermsOfService /></PageTransition>} />
        <Route path="/about" element={<PageTransition><About /></PageTransition>} />
        <Route path="/settings" element={<PageTransition><Settings /></PageTransition>} />
        <Route path="/notifications" element={<PageTransition><Notifications /></PageTransition>} />
        <Route path="/paywall" element={<PageTransition><Paywall /></PageTransition>} />
        <Route path="/admin/partners" element={<PageTransition><AdminPartners /></PageTransition>} />
        <Route path="/admin/email-analytics" element={<PageTransition><AdminEmailAnalytics /></PageTransition>} />
        <Route path="/partners" element={<PageTransition><Partners /></PageTransition>} />
        <Route path="/partner-success" element={<PageTransition><PartnerSuccess /></PageTransition>} />
        <Route path="/partner-dashboard" element={<PageTransition><PartnerDashboard /></PageTransition>} />
        {/* SEO Landing Pages */}
        <Route path="/ai-gift-app" element={<PageTransition><AIGiftApp /></PageTransition>} />
        <Route path="/gift-from-photo" element={<PageTransition><GiftFromPhoto /></PageTransition>} />
        <Route path="/photo-gift-finder" element={<PageTransition><PhotoGiftFinder /></PageTransition>} />
        <Route path="/gift-generator-iphone" element={<PageTransition><GiftGeneratorIPhone /></PageTransition>} />
        <Route path="/personalized-gift-app" element={<PageTransition><PersonalizedGiftApp /></PageTransition>} />
        <Route path="/ai-gift-finder" element={<PageTransition><AIGiftFinder /></PageTransition>} />
        <Route path="/gift-ideas-by-personality" element={<PageTransition><GiftIdeasByPersonality /></PageTransition>} />
        <Route path="/last-minute-gift-ideas" element={<PageTransition><LastMinuteGiftIdeas /></PageTransition>} />
        <Route path="/birthday-gift-ideas" element={<PageTransition><BirthdayGiftIdeas /></PageTransition>} />
        <Route path="/anniversary-gift-ideas" element={<PageTransition><AnniversaryGiftIdeas /></PageTransition>} />
        <Route path="/valentines-day-gifts" element={<PageTransition><ValentinesDayGifts /></PageTransition>} />
        <Route path="/christmas-gift-ideas" element={<PageTransition><ChristmasGiftIdeas /></PageTransition>} />
        <Route path="/gifts-for-her" element={<PageTransition><GiftsForHer /></PageTransition>} />
        <Route path="/gifts-for-him" element={<PageTransition><GiftsForHim /></PageTransition>} />
        <Route path="/best-white-elephant-gifts" element={<PageTransition><BestWhiteElephantGifts /></PageTransition>} />
        <Route path="/best-wedding-gifts" element={<PageTransition><BestWeddingGifts /></PageTransition>} />
        <Route path="/best-housewarming-gifts" element={<PageTransition><BestHousewarmingGifts /></PageTransition>} />
        <Route path="/best-birthday-gift-for-best-friend" element={<PageTransition><BestBirthdayGiftForBestFriend /></PageTransition>} />
        <Route path="/best-engagement-gifts" element={<PageTransition><BestEngagementGifts /></PageTransition>} />
        <Route path="/delete-account" element={<PageTransition><DeleteAccount /></PageTransition>} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </Suspense>
  );

  // Critical: On native we skip AnimatePresence (mode="wait" can leave a blank screen on resume)
  if (native) {
    return routes;
  }

  return <AnimatePresence mode="wait">{routes}</AnimatePresence>;
};

const RouterChooser = ({ children }: { children: React.ReactNode }) => {
  const native = useMemo(() => isNativeRuntime(), []);
  const Router = native ? HashRouter : BrowserRouter;
  return <Router>{children}</Router>;
};

const LAST_ACTIVE_KEY           = 'gistgift_last_active';
const LAST_RENDER_KEY           = 'gistgift_last_render';
const COLD_RESUME_THRESHOLD_MS  = 30000;   // 30 seconds
const LONG_IDLE_THRESHOLD_MS    = 900000;  // 15 minutes - trigger recovery
const RENDER_STALE_THRESHOLD_MS = 60000;   // 1 minute - render heartbeat stale

// Routes that should NOT trigger recovery (OAuth flows)
const PROTECTED_ROUTES = ['/auth/callback', '/native-callback', '/oauth/auth', '/boot'];

// Render heartbeat: writes timestamp to localStorage to detect stale UI.
// FIX: Also updates LAST_ACTIVE_KEY (moved from the removed NativeResumeOverlay)
// so AppStateRecovery continues to calculate idle time correctly.
const RenderHeartbeat = () => {
  const native = useMemo(() => isNativeRuntime(), []);

  useEffect(() => {
    if (!native) return;

    const updateTimestamps = () => {
      const now = Date.now().toString();
      localStorage.setItem(LAST_RENDER_KEY, now);
      localStorage.setItem(LAST_ACTIVE_KEY, now);
    };

    updateTimestamps();
    const interval = window.setInterval(updateTimestamps, 3000);

    return () => window.clearInterval(interval);
  }, [native]);

  return null;
};

// Hard rehydrate: forces a full page reload to /#/boot
const hardRehydrate = () => {
  console.log('[Recovery] Performing hard rehydrate to /#/boot');
  window.location.replace('/#/boot');
};

// Safety net: Force navigation to /boot if app was backgrounded too long
const AppStateRecovery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const native = useMemo(() => isNativeRuntime(), []);
  const recoveryInProgress = useRef(false);

  const forceRepaint = useCallback(() => {
    const root = document.documentElement;
    root.classList.add('__force-repaint');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.remove('__force-repaint');
      });
    });
  }, []);

  const performRecovery = useCallback(() => {
    if (recoveryInProgress.current) return;
    if (document.visibilityState !== "visible") return;

    const currentPath = location.pathname;

    if (PROTECTED_ROUTES.some(route => currentPath.startsWith(route))) {
      console.log(`[Recovery] Skipping recovery on protected route: ${currentPath}`);
      return;
    }

    const lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0', 10);
    const lastRender = parseInt(localStorage.getItem(LAST_RENDER_KEY) || '0', 10);
    const now        = Date.now();
    const elapsed    = now - lastActive;
    const renderAge  = now - lastRender;

    console.log(`[Recovery] Check: elapsed=${elapsed}ms, renderAge=${renderAge}ms, path=${currentPath}`);

    forceRepaint();

    const isLongIdle    = elapsed > LONG_IDLE_THRESHOLD_MS;
    const isRenderStale = renderAge > RENDER_STALE_THRESHOLD_MS && elapsed > COLD_RESUME_THRESHOLD_MS;

    if (isLongIdle || isRenderStale) {
      console.log(`[Recovery] Triggering recovery - longIdle: ${isLongIdle}, renderStale: ${isRenderStale}`);
      recoveryInProgress.current = true;

      localStorage.setItem(LAST_ACTIVE_KEY, now.toString());
      localStorage.setItem(LAST_RENDER_KEY, now.toString());

      navigate("/boot", { replace: true });

      window.setTimeout(() => {
        const currentRender = parseInt(localStorage.getItem(LAST_RENDER_KEY) || '0', 10);
        if (Date.now() - currentRender > 3000) {
          console.log('[Recovery] Navigation did not repaint, performing hard rehydrate');
          hardRehydrate();
        } else {
          recoveryInProgress.current = false;
        }
      }, 2000);
    }
  }, [location.pathname, navigate, forceRepaint]);

  useEffect(() => {
    if (!native) return;

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        performRecovery();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);

    let appStateListener: { remove: () => void } | null = null;
    let resumeListener:   { remove: () => void } | null = null;

    const setupCapacitorListeners = async () => {
      try {
        appStateListener = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          console.log(`[Recovery] Capacitor appStateChange: isActive=${isActive}`);
          if (isActive) performRecovery();
        });

        resumeListener = await CapacitorApp.addListener('resume', () => {
          console.log('[Recovery] Capacitor resume event');
          performRecovery();
        });

        console.log('[Recovery] Capacitor lifecycle listeners registered');
      } catch (e) {
        console.log('[Recovery] Capacitor listeners not available (web mode)');
      }
    };

    setupCapacitorListeners();

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      appStateListener?.remove();
      resumeListener?.remove();
    };
  }, [native, performRecovery]);

  return null;
};

const App = () => (
  <HelmetProvider>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <RouterChooser>
            <SpaRedirector />
            <CanonicalEnforcer />
            <RouterGate />
            <AppStateRecovery />
            <ProcessingProvider>
              <RenderHeartbeat />

              {/* Top safe area spacer */}
              <div
                style={{ height: "var(--safe-area-top, 0)" }}
                className="bg-background"
              />

              <AppHeader />
              <Suspense fallback={null}>
                <AdFlowManager />
              </Suspense>
              <AnimatedRoutes />

              {/* Bottom safe area spacer */}
              <div
                style={{ height: "var(--safe-area-bottom, 0)" }}
                className="bg-background"
              />
            </ProcessingProvider>
          </RouterChooser>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </HelmetProvider>
);

export default App;
